import { useCallback, useEffect, useMemo, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import {
  Sparkle, Confetti, Bed, Barbell, SwimmingPool, TennisBall, Buildings,
  Plus, X, ArrowRight, CalendarBlank, Trash, PencilSimple, Check,
} from "@phosphor-icons/react";
import { api } from "../lib/api";
import { useAuth } from "../lib/auth";
import { useDialog } from "../components/Dialog";
import { istToday } from "../lib/day";

const ICONS = { sparkles: Sparkle, balloon: Confetti, bed: Bed, dumbbell: Barbell, pool: SwimmingPool, tennis: TennisBall };
const amIcon = (a) => ICONS[a?.icon] || Buildings;
const inr = (n) => "₹" + Number(n || 0).toLocaleString("en-IN");
const STATUS = {
  PENDING: { label: "Awaiting approval", c: "amber" }, CONFIRMED: { label: "Confirmed", c: "lime" },
  DENIED: { label: "Declined", c: "pink" }, CANCELLED: { label: "Cancelled", c: "white" },
};
const CHIP = {
  amber: "border-neon-amber/30 bg-neon-amber/10 text-neon-amber", lime: "border-neon-lime/30 bg-neon-lime/10 text-neon-lime",
  pink: "border-neon-pink/30 bg-neon-pink/10 text-neon-pink", white: "border-white/15 bg-white/5 text-white/60",
};
const fmtDate = (d) => { try { return new Date(d).toLocaleDateString("en-IN", { weekday: "short", day: "numeric", month: "short" }); } catch { return d; } };
const todayStr = () => istToday(); // IST, not UTC — see lib/day.js

export default function Bookings() {
  const { user } = useAuth();
  const isStaff = ["MANAGER", "CHAIRPERSON", "ADMIN"].includes(user?.role);
  return isStaff ? <StaffBookings /> : <ResidentBookings />;
}

/* ───────── Resident: book + my bookings ───────── */
function ResidentBookings() {
  const [amenities, setAmenities] = useState([]);
  const [mine, setMine] = useState([]);
  const [book, setBook] = useState(null); // amenity being booked
  const { confirm } = useDialog();

  const load = useCallback(() => {
    api.get("/api/amenities").then(({ data }) => setAmenities(data.items || [])).catch(() => {});
    api.get("/api/bookings").then(({ data }) => setMine(data.items || [])).catch(() => {});
  }, []);
  useEffect(() => { load(); }, [load]);

  async function cancel(b) {
    if (!(await confirm({ title: "Cancel booking?", message: `${b.amenityName} · ${fmtDate(b.date)} · ${b.slot}`, confirmText: "Cancel booking", danger: true }))) return;
    await api.post(`/api/bookings/${b.id}/cancel`); load();
  }

  return (
    <div className="space-y-5 font-sans" data-testid="bookings-resident">
      <div>
        <p className="text-[11px] font-bold uppercase tracking-[0.32em] text-white/50">Amenities</p>
        <h1 className="font-display text-3xl font-extrabold text-white">Book a facility</h1>
        <p className="mt-1 text-sm text-white/55">Reserve the clubhouse, courts and more.</p>
      </div>

      <div className="grid grid-cols-2 gap-3 sm:grid-cols-3">
        {amenities.map((a) => {
          const Icon = amIcon(a);
          return (
            <button key={a.id} onClick={() => setBook(a)} data-testid={`amenity-${a.id}`}
              className="group relative overflow-hidden rounded-3xl border border-white/10 bg-white/[0.03] p-4 text-left transition-all hover:border-white/20 active:scale-[0.98]">
              <div className="mb-6 inline-flex h-11 w-11 items-center justify-center rounded-2xl bg-neon-cyan/15 text-neon-cyan"><Icon size={22} weight="duotone" /></div>
              <p className="font-display text-[15px] font-semibold text-white">{a.name}</p>
              <p className="text-[11px] text-white/55">{a.charge > 0 ? inr(a.charge) : "Free"}{a.requiresApproval ? " · approval" : ""}</p>
              <ArrowRight weight="bold" className="absolute bottom-4 right-4 text-white/30 group-hover:text-white" />
            </button>
          );
        })}
      </div>

      <div>
        <p className="mb-2 text-[11px] font-bold uppercase tracking-[0.22em] text-white/55">My bookings</p>
        {mine.length === 0 ? <div className="card p-6 text-center text-sm text-white/45">No bookings yet.</div>
          : <div className="space-y-2">{mine.map((b) => <BookingRow key={b.id} b={b} onCancel={() => cancel(b)} />)}</div>}
      </div>

      <AnimatePresence>{book && <BookSheet amenity={book} onClose={() => setBook(null)} onBooked={() => { setBook(null); load(); }} />}</AnimatePresence>
    </div>
  );
}

function BookingRow({ b, onCancel, staff }) {
  const st = STATUS[b.status] || { label: b.status, c: "white" };
  const active = b.status === "PENDING" || b.status === "CONFIRMED";
  return (
    <div className="card flex items-center gap-3 p-3.5" data-testid={`booking-${b.id}`}>
      <div className="min-w-0 flex-1">
        <div className="flex items-center gap-2">
          <p className="truncate font-display text-[14px] font-semibold text-white">{b.amenityName}</p>
          <span className={`shrink-0 rounded-md border px-1.5 py-0.5 text-[10px] font-bold ${CHIP[st.c]}`}>{st.label}</span>
        </div>
        <p className="truncate text-[12px] text-white/55">{fmtDate(b.date)} · {b.slot}{staff ? ` · ${b.flatLabel}` : ""}{b.charge > 0 ? ` · ${inr(b.charge)}` : ""}</p>
      </div>
      {active && <button onClick={onCancel} className="rounded-xl bg-white/5 px-3 py-1.5 text-[12px] font-bold text-white/70 hover:text-neon-pink">Cancel</button>}
    </div>
  );
}

function BookSheet({ amenity, onClose, onBooked }) {
  const Icon = amIcon(amenity);
  const [date, setDate] = useState(todayStr());
  const [slot, setSlot] = useState(null);
  const [full, setFull] = useState([]);
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState(null);

  useEffect(() => {
    setSlot(null);
    api.get(`/api/bookings/slots?amenityId=${amenity.id}&date=${date}`).then(({ data }) => setFull(data.full || [])).catch(() => setFull([]));
  }, [amenity.id, date]);

  async function submit() {
    if (!slot) { setErr("Pick a slot."); return; }
    setBusy(true); setErr(null);
    try { await api.post("/api/bookings", { amenityId: amenity.id, date, slot, note: note || null }); onBooked(); }
    catch (e) {
      const d = e?.response?.data?.detail;
      setErr(d === "slot_full" ? "That slot is full." : d === "already_booked" ? "You already booked this slot." : "Could not book. Try again.");
      setBusy(false);
    }
  }

  return (
    <motion.div className="fixed inset-0 z-[80] flex items-end justify-center sm:items-center" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}>
      <div className="absolute inset-0 bg-black/70 backdrop-blur-sm" onClick={onClose} />
      <motion.div initial={{ y: 60, opacity: 0 }} animate={{ y: 0, opacity: 1 }} exit={{ y: 60, opacity: 0 }}
        className="relative w-full max-w-md overflow-y-auto rounded-t-[2rem] border border-white/10 bg-ink-900 p-6 pb-safe sm:rounded-[2rem]" data-testid="book-sheet">
        <div className="mx-auto mb-3 h-1 w-10 rounded-full bg-white/15 sm:hidden" />
        <div className="flex items-center gap-3">
          <div className="flex h-11 w-11 items-center justify-center rounded-2xl bg-neon-cyan/15 text-neon-cyan"><Icon size={22} weight="duotone" /></div>
          <div className="flex-1"><h3 className="font-display text-xl font-bold text-white">{amenity.name}</h3>
            <p className="text-[12px] text-white/55">{amenity.charge > 0 ? inr(amenity.charge) : "Free"}{amenity.requiresApproval ? " · needs office approval" : " · instant"}</p></div>
          <button onClick={onClose} className="rounded-full bg-white/5 p-1.5 text-white/60"><X size={16} weight="bold" /></button>
        </div>
        <div className="mt-4 space-y-4">
          <div>
            <label className="label">Date</label>
            <input type="date" className="field" min={todayStr()} value={date} onChange={(e) => setDate(e.target.value)} data-testid="book-date" />
          </div>
          <div>
            <label className="label">Slot</label>
            <div className="flex flex-wrap gap-1.5">
              {(amenity.slots || []).map((s) => {
                const isFull = full.includes(s);
                return (
                  <button key={s} disabled={isFull} onClick={() => setSlot(s)} data-testid={`slot-${s}`}
                    className={`rounded-xl border px-3 py-2 text-[12px] font-semibold transition ${isFull ? "cursor-not-allowed border-white/5 bg-white/[0.02] text-white/25 line-through" : slot === s ? "border-neon-cyan/60 bg-neon-cyan/15 text-neon-cyan" : "border-white/10 bg-white/[0.03] text-white/70"}`}>
                    {s}{isFull ? " · full" : ""}
                  </button>
                );
              })}
            </div>
          </div>
          <div><label className="label">Note (optional)</label>
            <input className="field" value={note} onChange={(e) => setNote(e.target.value)} placeholder="e.g. Birthday party, 30 guests" /></div>
          {err && <p className="rounded-xl border border-neon-pink/30 bg-neon-pink/10 px-3 py-2 text-sm text-neon-pink">{err}</p>}
          <button disabled={!slot || busy} onClick={submit} data-testid="book-submit" className="btn-primary w-full disabled:opacity-50">
            {busy ? "Booking…" : <>{amenity.requiresApproval ? "Request booking" : "Book now"} <ArrowRight weight="bold" className="ml-1" /></>}
          </button>
        </div>
      </motion.div>
    </motion.div>
  );
}

/* ───────── Staff: manage bookings + amenities ───────── */
function StaffBookings() {
  const [tab, setTab] = useState("bookings");
  const [bookings, setBookings] = useState([]);
  const [amenities, setAmenities] = useState([]);
  const [editAm, setEditAm] = useState(null); // amenity or {} for new
  const { confirm } = useDialog();

  const load = useCallback(() => {
    api.get("/api/bookings").then(({ data }) => setBookings(data.items || [])).catch(() => {});
    api.get("/api/amenities?manage=true").then(({ data }) => setAmenities(data.items || [])).catch(() => {});
  }, []);
  useEffect(() => { load(); const id = setInterval(load, 12000); return () => clearInterval(id); }, [load]);

  async function decide(b, decision) { await api.post(`/api/bookings/${b.id}/decision`, { decision }); load(); }
  async function cancel(b) {
    if (!(await confirm({ title: "Cancel this booking?", message: `${b.amenityName} · ${b.flatLabel} · ${fmtDate(b.date)}`, confirmText: "Cancel", danger: true }))) return;
    await api.post(`/api/bookings/${b.id}/cancel`); load();
  }
  async function removeAm(a) {
    if (!(await confirm({ title: "Remove amenity?", message: `${a.name} will no longer be bookable.`, confirmText: "Remove", danger: true }))) return;
    await api.post(`/api/amenities/${a.id}/remove`); load();
  }

  const pending = bookings.filter((b) => b.status === "PENDING");

  return (
    <div className="space-y-5 font-sans" data-testid="bookings-staff">
      <div className="flex items-end justify-between">
        <div>
          <p className="text-[11px] font-bold uppercase tracking-[0.32em] text-white/50">Amenities</p>
          <h1 className="font-display text-3xl font-extrabold text-white">Bookings</h1>
        </div>
      </div>
      <div className="flex gap-1.5 rounded-2xl border border-white/10 bg-white/[0.03] p-1.5">
        {[["bookings", `Bookings${pending.length ? ` (${pending.length})` : ""}`], ["amenities", "Amenities"]].map(([k, l]) => (
          <button key={k} onClick={() => setTab(k)} className={`flex-1 rounded-xl px-2 py-2 text-[13px] font-bold transition ${tab === k ? "bg-neon-cyan text-black shadow-glow-cyan" : "text-white/65"}`}>{l}</button>
        ))}
      </div>

      {tab === "bookings" ? (
        bookings.length === 0 ? <div className="card p-8 text-center text-sm text-white/45">No bookings.</div>
          : <div className="space-y-2">{bookings.map((b) => (
            <div key={b.id} className="card p-3.5" data-testid={`sbooking-${b.id}`}>
              <div className="flex items-center gap-3">
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-2">
                    <p className="truncate font-display text-[14px] font-semibold text-white">{b.amenityName}</p>
                    <span className={`shrink-0 rounded-md border px-1.5 py-0.5 text-[10px] font-bold ${CHIP[(STATUS[b.status] || {}).c || "white"]}`}>{(STATUS[b.status] || {}).label || b.status}</span>
                  </div>
                  <p className="truncate text-[12px] text-white/55">{b.flatLabel} · {fmtDate(b.date)} · {b.slot}{b.note ? ` · ${b.note}` : ""}</p>
                </div>
                {b.status === "PENDING" && (
                  <div className="flex gap-1.5">
                    <button onClick={() => decide(b, "DENY")} className="rounded-xl border border-neon-pink/30 bg-neon-pink/10 px-3 py-1.5 text-[12px] font-bold text-neon-pink">Deny</button>
                    <button onClick={() => decide(b, "APPROVE")} className="rounded-xl bg-neon-lime px-3 py-1.5 text-[12px] font-bold text-black">Approve</button>
                  </div>
                )}
                {b.status === "CONFIRMED" && <button onClick={() => cancel(b)} className="rounded-xl bg-white/5 px-3 py-1.5 text-[12px] font-bold text-white/70 hover:text-neon-pink">Cancel</button>}
              </div>
            </div>
          ))}</div>
      ) : (
        <div className="space-y-2">
          <button onClick={() => setEditAm({})} className="flex w-full items-center justify-center gap-2 rounded-2xl border border-dashed border-white/20 bg-white/[0.02] py-3 text-sm font-bold text-white/80 hover:border-neon-cyan/40"><Plus size={16} weight="bold" /> Add amenity</button>
          {amenities.map((a) => {
            const Icon = amIcon(a);
            return (
              <div key={a.id} className={`card flex items-center gap-3 p-3.5 ${a.active ? "" : "opacity-50"}`}>
                <div className="flex h-10 w-10 items-center justify-center rounded-2xl bg-neon-cyan/15 text-neon-cyan"><Icon size={20} weight="duotone" /></div>
                <div className="min-w-0 flex-1">
                  <p className="truncate font-display text-[14px] font-semibold text-white">{a.name}{!a.active ? " · removed" : ""}</p>
                  <p className="truncate text-[12px] text-white/55">{(a.slots || []).length} slots · cap {a.capacityPerSlot} · {a.charge > 0 ? inr(a.charge) : "Free"}{a.requiresApproval ? " · approval" : ""}</p>
                </div>
                <button onClick={() => setEditAm(a)} className="rounded-lg p-1.5 text-white/40 hover:text-white"><PencilSimple size={15} weight="bold" /></button>
                {a.active && <button onClick={() => removeAm(a)} className="rounded-lg p-1.5 text-white/40 hover:text-neon-pink"><Trash size={15} weight="bold" /></button>}
              </div>
            );
          })}
        </div>
      )}

      <AnimatePresence>{editAm && <AmenitySheet amenity={editAm.id ? editAm : null} onClose={() => setEditAm(null)} onSaved={() => { setEditAm(null); load(); }} />}</AnimatePresence>
    </div>
  );
}

function AmenitySheet({ amenity, onClose, onSaved }) {
  const [name, setName] = useState(amenity?.name || "");
  const [slots, setSlots] = useState((amenity?.slots || []).join("\n"));
  const [requiresApproval, setReq] = useState(amenity ? !!amenity.requiresApproval : true);
  const [capacity, setCapacity] = useState(amenity ? String(amenity.capacityPerSlot) : "1");
  const [charge, setCharge] = useState(amenity ? String(amenity.charge || 0) : "0");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState(null);
  const slotList = useMemo(() => slots.split("\n").map((s) => s.trim()).filter(Boolean), [slots]);
  const valid = name.trim().length >= 2 && slotList.length >= 1;

  async function submit() {
    if (!valid) { setErr("Enter a name and at least one slot."); return; }
    setBusy(true); setErr(null);
    const body = { name: name.trim(), icon: amenity?.icon || "sparkles", slots: slotList, requiresApproval, capacityPerSlot: parseInt(capacity) || 1, charge: parseFloat(charge) || 0 };
    try { if (amenity) await api.patch(`/api/amenities/${amenity.id}`, body); else await api.post("/api/amenities", body); onSaved(); }
    catch { setErr("Could not save."); setBusy(false); }
  }

  return (
    <motion.div className="fixed inset-0 z-[80] flex items-end justify-center sm:items-center" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}>
      <div className="absolute inset-0 bg-black/70 backdrop-blur-sm" onClick={onClose} />
      <motion.div initial={{ y: 60, opacity: 0 }} animate={{ y: 0, opacity: 1 }} exit={{ y: 60, opacity: 0 }}
        className="relative w-full max-w-md overflow-y-auto rounded-t-[2rem] border border-white/10 bg-ink-900 p-6 pb-safe sm:rounded-[2rem]" data-testid="amenity-sheet">
        <div className="flex items-center justify-between">
          <h3 className="font-display text-xl font-bold text-white">{amenity ? "Edit amenity" : "Add amenity"}</h3>
          <button onClick={onClose} className="rounded-full bg-white/5 p-1.5 text-white/60"><X size={16} weight="bold" /></button>
        </div>
        <div className="mt-4 space-y-4">
          <div><label className="label">Name</label><input className="field" value={name} onChange={(e) => setName(e.target.value)} placeholder="e.g. Badminton Court" data-testid="amenity-name" /></div>
          <div><label className="label">Slots (one per line)</label><textarea className="field min-h-[110px]" value={slots} onChange={(e) => setSlots(e.target.value)} placeholder={"6–7 AM\n7–8 AM\nEvening (5–9 PM)"} /></div>
          <div className="grid grid-cols-2 gap-2">
            <div><label className="label">Capacity / slot</label><input className="field" inputMode="numeric" value={capacity} onChange={(e) => setCapacity(e.target.value.replace(/\D/g, ""))} /></div>
            <div><label className="label">Charge (₹)</label><input className="field" inputMode="numeric" value={charge} onChange={(e) => setCharge(e.target.value.replace(/[^0-9.]/g, ""))} /></div>
          </div>
          <button onClick={() => setReq((v) => !v)} className="flex w-full items-center justify-between rounded-2xl border border-white/10 bg-white/[0.03] px-4 py-3">
            <span className="text-[13px] font-semibold text-white">Requires office approval</span>
            <span className={`flex h-6 w-11 items-center rounded-full px-0.5 ${requiresApproval ? "bg-neon-cyan" : "bg-white/15"}`}><span className={`h-5 w-5 rounded-full bg-white transition-transform ${requiresApproval ? "translate-x-5" : ""}`} /></span>
          </button>
          {err && <p className="rounded-xl border border-neon-pink/30 bg-neon-pink/10 px-3 py-2 text-sm text-neon-pink">{err}</p>}
          <button disabled={!valid || busy} onClick={submit} className="btn-primary w-full disabled:opacity-50">{busy ? "Saving…" : <><Check size={16} weight="bold" className="mr-1.5" /> {amenity ? "Save" : "Add amenity"}</>}</button>
        </div>
      </motion.div>
    </motion.div>
  );
}
